import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { setTitle } from "../../../helpers/MetaTag";
import { Check } from "lucide-react";

/**
 * /etsy/done — the end of the Etsy flow.
 *
 * A Shopify customer lands on a checkout confirmation here. An Etsy buyer has
 * nothing left to pay, so this exists to say the one thing they actually need
 * to know: it's with us now, and nothing else is required of them.
 *
 * It deliberately has NO navigation — not even the site menu. The buyer came
 * from one Etsy order to build one book; every link out of here (Home, How It
 * Works, My Books) is an invitation to wander back into a wizard whose session
 * was torn down on submit. It uses the same EtsyLayout as /etsy: a centred
 * logo that goes nowhere, and a copyright-only footer.
 *
 * Back is trapped. The submit that brought the buyer here cleared their
 * session, so the wizard page behind this one is gone. Pressing Back would
 * otherwise land on the Shopify sign-in, which they have never seen. Instead a
 * duplicate history entry is pushed on mount, and a Back press (popstate)
 * flips this page into its "no longer available" state and re-pushes, so the
 * buyer can press Back all day and only ever see that message. Jumping
 * straight to a wizard URL from the history list is caught by UserRoute, which
 * sends them here with ?expired=1.
 *
 * `?format=digital` swaps in the digital download's wording: nothing is
 * printed or shipped, so "print and bind" and "Etsy emails you tracking" would
 * be wrong. `?format=combined` (printed book + PDF) keeps the printing steps
 * and adds the emailed download. The format has to come from the URL — the
 * session that knew what was bought is torn down before this page loads.
 *
 * `?copies=N` (N above 1): the buyer ordered N printed copies of their one
 * book, so the printed and combined wording turns plural ("your 3 copies").
 * A digital order prints nothing and ignores it.
 */
const COPY = {
  printed: {
    title: "Your book is on its way",
    // The printed wording is the design's (EtsyDone); combined and digital have no design and keep the app's.
    lead: "We've got your photos and your book is in line for printing. There's nothing else you need to do.",
    steps: ["We print and bind your book.", "Etsy emails you tracking when it ships."],
  },
  combined: {
    title: "Your book is on its way",
    lead: "Thanks! We've got your photos. Your book is queued for printing, and we're making your PDF copy now. Nothing else is needed from you.",
    steps: [
      "We email you a download link as soon as your PDF is ready.",
      "We print and bind your book.",
      "Etsy emails you tracking as soon as it ships.",
    ],
  },
  digital: {
    title: "Your digital book is on its way",
    lead: "Thanks! We've got your photos, and we're making your coloring book now. Nothing else is needed from you.",
    steps: [
      "We turn your photos into your coloring book.",
      "We email you a download link as soon as it's ready.",
    ],
  },
};
/** The plural wording for N printed copies of the one book. */
const manyCopies = (format, n) =>
  format === "combined"
    ? {
        title: "Your books are on their way",
        lead: `Thanks! We've got your photos. Your ${n} copies are queued for printing, and we're making your PDF now. Nothing else is needed from you.`,
        steps: [
          "We email you a download link as soon as your PDF is ready.",
          `We print and bind your ${n} copies.`,
          "Etsy emails you tracking as soon as they ship.",
        ],
      }
    : {
        title: "Your books are on their way",
        lead: `We've got your photos and your ${n} copies are in line for printing. There's nothing else you need to do.`,
        steps: [`We print and bind your ${n} copies.`, "Etsy emails you tracking when they ship."],
      };

export const etsyDoneCopy = (search) => {
  const params = new URLSearchParams(search);
  const format = COPY[params.get("format")] ? params.get("format") : "printed";
  const copies = Number.parseInt(params.get("copies"), 10);
  if (format !== "digital" && copies > 1) return manyCopies(format, copies);
  return COPY[format];
};

const EtsyDone = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [expired, setExpired] = useState(() => params.get("expired") === "1");
  const copy = etsyDoneCopy(location.search);

  useEffect(() => {
    setTitle(expired ? "MemoryDoodle" : `${copy.title} | MemoryDoodle`);
  }, [expired, copy.title]);

  useEffect(() => {
    const here = window.location.pathname + window.location.search;
    try {
      window.history.pushState(null, "", here);
    } catch (_) {
      return undefined;
    }
    const onPopState = () => {
      setExpired(true);
      try {
        window.history.pushState(null, "", here);
      } catch (_) {
        /* trap gone; the message is still showing */
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // ---- on the redesign (docs/design/HANDOFF.md: EtsyDone, TabEtsyDone, DeskEtsyDone)
  return (
    <main className="mx-auto w-full max-w-[480px] px-6 pb-8 pt-14 md:max-w-[568px] md:pb-12 md:pt-[120px] lg:pb-16 lg:pt-24">
      {expired ? (
        <div className="flex flex-col items-center gap-3.5 text-center">
          <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">This page is no longer available</h1>
          <p className="m-0 text-base leading-relaxed text-md-muted md:text-lg">Your book has already been sent to us, so there's nothing more to do here.</p>
          <a href="/" className="font-semibold text-md-pink-dark underline">
            Go to the MemoryDoodle homepage
          </a>
        </div>
      ) : (
        <div className="flex flex-col gap-7">
          <div className="flex flex-col items-center gap-3.5 text-center">
            <span aria-hidden="true" className="mb-1.5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-green-100 text-green-800 md:h-[84px] md:w-[84px]">
              <Check className="h-8 w-8 md:h-9 md:w-9" strokeWidth={2.5} />
            </span>
            <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">{copy.title}</h1>
            <p className="m-0 text-base leading-normal text-md-muted md:text-lg">{copy.lead}</p>
          </div>
          <section aria-labelledby="etsy-done-next" className="flex flex-col gap-4 rounded-2xl border border-md-pink-light-1 bg-white px-[22px] pb-6 pt-[22px]">
            <h2 id="etsy-done-next" className="m-0 font-sans text-[13px] font-bold uppercase tracking-[0.06em] text-md-muted">
              What happens next
            </h2>
            <ol className="m-0 flex list-none flex-col gap-3.5 p-0">
              {copy.steps.map((step, i) => (
                <li key={step} className="flex items-center gap-3.5">
                  <span aria-hidden="true" className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-md-pink/10 text-sm font-bold text-md-pink-darker md:h-[34px] md:w-[34px] lg:h-8 lg:w-8">
                    {i + 1}
                  </span>
                  <span className="text-base leading-[1.45] text-md-dark md:text-lg lg:text-[17px]">{step}</span>
                </li>
              ))}
            </ol>
          </section>
          <p className="m-0 text-center text-sm leading-normal text-md-muted">Questions about your order? Message us on Etsy.</p>
          <p className="m-0 text-center text-xs leading-normal text-md-muted md:text-[13px]">
            Etsy is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.
          </p>
        </div>
      )}
    </main>
  );
};

export default EtsyDone;
